import {
  Copy,
  Mail,
  MessageCircle,
  Send,
  Share2,
  type LucideIcon,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  getShareMethods,
  type ShareContent,
  type ShareMethod,
} from '@/utils/share';

interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  content: ShareContent;
}

const iconMap: Record<string, LucideIcon> = {
  Share2,
  Mail,
  MessageCircle,
  Send,
  Copy,
};

export function ShareDialog({ open, onOpenChange, content }: ShareDialogProps) {
  const [isSharing, setIsSharing] = useState<ShareMethod | null>(null);

  const handleShare = useCallback(
    async (
      methodId: ShareMethod,
      action: (content: ShareContent) => Promise<void> | void,
    ) => {
      setIsSharing(methodId);
      try {
        await action(content);

        // Show success toast for copy action
        if (methodId === 'copy') {
          toast.success('Link copied', {
            description: 'The link has been copied to your clipboard.',
          });
        }

        // Close dialog after successful share (except for copy)
        if (methodId !== 'copy') {
          onOpenChange(false);
        }
      } catch (error) {
        // Only show error if it's not a user cancellation
        if (error instanceof Error && error.message) {
          toast.error('Share failed', {
            description: error.message,
          });
        }
      } finally {
        setIsSharing(null);
      }
    },
    [content, onOpenChange],
  );

  const shareMethods = getShareMethods();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share</DialogTitle>
          <DialogDescription>
            Share "{content.title}" via your preferred method
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2 py-4">
          {shareMethods.map((method) => {
            const Icon = iconMap[method.icon];
            return (
              <Button
                key={method.id}
                variant="outline"
                className="justify-start"
                onClick={() => handleShare(method.id, method.action)}
                disabled={isSharing !== null}
              >
                {Icon && <Icon className="size-4" />}
                {method.label}
              </Button>
            );
          })}
        </div>

        {/* URL Preview */}
        <div className="bg-muted rounded-md p-3">
          <div className="text-muted-foreground mb-1 text-xs">Link</div>
          <div className="text-sm break-all">{content.url}</div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
